import React, { useState, useMemo } from 'react';
import { Button, useToast } from '@lognai/synapse-design-system-v2';
import { useAiBundle } from '../../hooks/use-bundle';
import { useApplyAiBundle, useRejectAiDraft } from '../../hooks/use-bundle-mutations';
import { DraftRow } from './DraftRow';
import { selectWithCascade, deselectWithCascade } from './auto-select-dependencies';
import { WorkflowProgressCard } from '../WorkflowProgressCard';

export interface BundleReviewPanelProps {
  bundleId: string | null;
  open: boolean;
  onClose: () => void;
  /** Hide Apply/Reject controls; render as a view-only inspector. Default: false. */
  readOnly?: boolean;
}

export const BundleReviewPanel: React.FC<BundleReviewPanelProps> = ({ bundleId, open, onClose, readOnly = false }) => {
  const { data, isPending } = useAiBundle(bundleId);
  const applyBundle = useApplyAiBundle();
  const rejectDraft = useRejectAiDraft(bundleId ?? undefined);
  const { toast } = useToast();

  // Local state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmedHighRiskIds, setConfirmedHighRiskIds] = useState<Set<string>>(new Set());

  const items = data?.items ?? [];

  // 4e.B.5 — when this bundle's drafts came from a workflow run, surface the
  // WorkflowProgressCard above the per-row list so the user sees the full
  // plan + per-step status as they review.
  const workflowItem = items.find((it) => it.workflowRunId);
  const workflowRunId = workflowItem?.workflowRunId ?? null;
  const workflowId = workflowItem?.workflowId ?? null;

  // Apply readiness — every selected high-risk item must have been confirmed
  const applyReady = useMemo(() => {
    if (selectedIds.size === 0) return false;
    for (const id of selectedIds) {
      const item = items.find((i) => i.id === id);
      if (!item) continue;
      if (item.riskBand === 'high' && !confirmedHighRiskIds.has(id)) return false;
    }
    return true;
  }, [selectedIds, confirmedHighRiskIds, items]);

  // Compute which rows are blocked because a predecessor is in a non-live state.
  // Pre-computed once per render so each DraftRow can read its own blocker reason
  // without re-walking the graph.
  const blockedByMap = useMemo(() => {
    const m = new Map<string, string>();
    const byIndex = new Map<number, typeof items[number]>();
    for (const it of items) {
      if (it.draftIndex !== undefined) byIndex.set(it.draftIndex, it);
    }
    for (const it of items) {
      for (const p of it.dependsOn ?? []) {
        const pred = byIndex.get(p);
        if (!pred) continue;
        if (
          pred.status === 'failed' ||
          pred.status === 'rejected' ||
          pred.status === 'skipped' ||
          pred.status === 'rolled_back'
        ) {
          m.set(it.id, `Predecessor failed (draft #${p}). Apply will skip this draft.`);
          break;
        }
      }
    }
    return m;
  }, [items]);

  // Bail-out AFTER all hooks have run — moving the early return above the
  // second useMemo would change the hook count between renders and crash
  // React with "Rendered more hooks than during the previous render."
  if (!open || !bundleId) return null;

  const onToggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.has(id)
        ? deselectWithCascade(prev, id, items)
        : selectWithCascade(prev, id, items),
    );
  };

  const onConfirmHighRisk = (id: string, confirmed: boolean) => {
    setConfirmedHighRiskIds((prev) => {
      const next = new Set(prev);
      if (confirmed) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const onApply = async () => {
    try {
      await applyBundle.mutateAsync(bundleId);
      toast({ title: 'Bundle applied', description: `${selectedIds.size} drafts applied.` });
      onClose();
    } catch (err) {
      toast({ title: 'Apply failed', description: (err as Error).message, tone: 'destructive' });
    }
  };

  const onRejectAll = async () => {
    for (const item of items) {
      if (item.status === 'pending' || item.status === 'approved') {
        try {
          await rejectDraft.mutateAsync({ draftId: item.id });
        } catch { /* best-effort */ }
      }
    }
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label="Review draft bundle"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 520,
        backgroundColor: 'var(--synapse-surface, white)',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
        zIndex: 50,
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ padding: 16, borderBottom: '1px solid var(--synapse-border, #e5e7eb)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Review changes</div>
          {data?.bundle ? (
            <div style={{ fontSize: 12, color: 'var(--synapse-text-muted, #6b7280)' }}>
              {data.bundle.itemCount} items · {data.bundle.riskBandMax} risk
            </div>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {workflowRunId ? (
          <WorkflowProgressCard
            workflowRunId={workflowRunId}
            workflowDisplayName={workflowId ?? undefined}
            variant="expanded"
          />
        ) : null}
        {isPending ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--synapse-text-muted, #6b7280)' }}>Loading bundle…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--synapse-text-muted, #6b7280)' }}>No draft items.</div>
        ) : (
          items.map((item) => (
            <DraftRow
              key={item.id}
              item={item}
              selected={selectedIds.has(item.id)}
              onToggleSelect={() => onToggleSelect(item.id)}
              highRiskConfirmed={confirmedHighRiskIds.has(item.id)}
              onConfirmHighRisk={(c) => onConfirmHighRisk(item.id, c)}
              readOnly={readOnly}
              blockedBy={blockedByMap.get(item.id)}
            />
          ))
        )}
      </div>

      {/* Footer */}
      {!readOnly && (
        <div style={{ padding: 16, borderTop: '1px solid var(--synapse-border, #e5e7eb)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onRejectAll} disabled={items.length === 0}>Reject all</Button>
          <Button
            variant="primary"
            onClick={onApply}
            disabled={!applyReady || applyBundle.isPending}
            aria-disabled={!applyReady}
          >
            {applyBundle.isPending ? 'Applying…' : `Apply selected (${selectedIds.size})`}
          </Button>
        </div>
      )}
    </div>
  );
};
